import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from '../utils/api';
import {
  AlertTriangle, CheckCircle, TrendingUp, RefreshCw,
  ArrowUpRight, FileText, Users, Database, History,
  BarChart3, ChevronRight, Shield,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PLAN_LIMITS = {
  starter:      { aars: 50,   users: 25,  historic_docs: 150, storage_gb: 10 },
  professional: { aars: 200,  users: 100, historic_docs: 300, storage_gb: 50 },
  enterprise:   { aars: null, users: null, historic_docs: null, storage_gb: null },
};

const PLAN_META = {
  starter:      { label: 'Small Agency',    badge: 'bg-slate-100 text-slate-700' },
  professional: { label: 'Mid-Size Agency', badge: 'bg-blue-100 text-blue-700' },
  enterprise:   { label: 'Enterprise',      badge: 'bg-purple-100 text-purple-700' },
};

function PlanBadge({ plan }) {
  const m = PLAN_META[plan] || PLAN_META.starter;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.badge}`}>
      {m.label}
    </span>
  );
}

function UsageBar({ used, limit }) {
  if (!limit) {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-2 bg-purple-100 rounded-full">
          <div className="h-full w-1/4 bg-purple-300 rounded-full" />
        </div>
        <span className="text-xs text-purple-500 font-medium">Unlimited</span>
      </div>
    );
  }
  const pct = Math.min((used / limit) * 100, 100);
  const color = pct >= 100 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function MetricRow({ icon: Icon, label, used, limit, pct, iconColor }) {
  const atLimit = limit && used >= limit;
  const nearLimit = !atLimit && pct >= 75;
  const textColor = atLimit ? 'text-red-600' : nearLimit ? 'text-amber-600' : 'text-gray-700';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className={`text-sm font-semibold ${textColor}`}>
          {used.toLocaleString()} {limit ? `/ ${limit.toLocaleString()}` : '/ ∞'}
          {limit ? <span className="text-xs text-gray-400 font-normal ml-1">({pct}%)</span> : null}
        </span>
      </div>
      <UsageBar used={used} limit={limit} />
    </div>
  );
}

export function SuperAdminUsageTab() {
  const { organization } = useContext(AuthContext);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [period, setPeriod] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth('/usage/all', organization.organizationId);
      setOrgs(data.organizations || []);
      setPeriod(data.billing_period || '');
    } catch { toast.error('Failed to load usage data'); }
    setLoading(false);
  };

  const changePlan = async (orgId, newPlan) => {
    setUpdating(orgId);
    try {
      await authenticatedFetch(`${API_ENDPOINT}/usage/plan`, {
        method: 'PUT',
        body: JSON.stringify({ organization_id: orgId, plan_tier: newPlan }),
      });
      toast.success('Plan updated successfully');
      load();
    } catch { toast.error('Failed to update plan'); }
    setUpdating(null);
  };

  const atLimitCount = orgs.filter(o => o.at_aar_limit).length;
  const warningCount = orgs.filter(o => !o.at_aar_limit && o.usage.aar_pct >= 75).length;
  const totalAARs = orgs.reduce((s, o) => s + o.usage.aars_used, 0);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw className="w-5 h-5 text-blue-500 animate-spin mr-3" />
      <span className="text-gray-500 text-sm">Loading usage data…</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <span>Usage Monitor</span>
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Annual billing period: <span className="font-semibold">{period}</span>
          </p>
        </div>
        <button onClick={load}
          className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition">
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Orgs', value: orgs.length, color: 'text-gray-900', bg: 'bg-white' },
          { label: 'At Limit', value: atLimitCount, color: atLimitCount > 0 ? 'text-red-600' : 'text-gray-900', bg: atLimitCount > 0 ? 'bg-red-50 border-red-200' : 'bg-white' },
          { label: 'Near Limit (≥75%)', value: warningCount, color: warningCount > 0 ? 'text-amber-600' : 'text-gray-900', bg: warningCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white' },
          { label: 'Total AARs This Year', value: totalAARs, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
        ].map(c => (
          <div key={c.label} className={`border rounded-xl p-4 shadow-sm ${c.bg}`}>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{c.label}</p>
            <p className={`text-3xl font-bold mt-1 ${c.color}`}>{c.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {orgs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Database className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No usage data yet for {period}.</p>
          <p className="text-xs mt-1">Records are created when orgs first generate an AAR.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                {['Organization', 'Plan', 'AARs', 'Historic Docs', 'Users', 'Storage', 'Status', 'Change Plan'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orgs.map(org => {
                const u = org.usage;
                const row = org.at_aar_limit ? 'bg-red-50' : u.aar_pct >= 75 ? 'bg-amber-50' : '';
                return (
                  <tr key={org.organization_id} className={`hover:bg-gray-50 transition ${row}`}>
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900 text-sm">{org.organization_name}</div>
                      <div className="text-xs text-gray-400 font-mono mt-0.5">{org.organization_id}</div>
                    </td>
                    <td className="px-4 py-4"><PlanBadge plan={org.plan_tier} /></td>
                    <td className="px-4 py-4 min-w-[140px]">
                      <UsageBar used={u.aars_used} limit={u.aar_limit} />
                      <p className="text-xs text-gray-500 mt-1">{u.aars_used} / {u.aar_limit ?? '∞'}{u.aar_limit ? ` (${u.aar_pct}%)` : ''}</p>
                    </td>
                    <td className="px-4 py-4 min-w-[140px]">
                      <UsageBar used={u.historic_docs_used} limit={u.historic_limit} />
                      <p className="text-xs text-gray-500 mt-1">{u.historic_docs_used} / {u.historic_limit ?? '∞'}</p>
                    </td>
                    <td className="px-4 py-4 min-w-[120px]">
                      <UsageBar used={u.users_used} limit={u.user_limit} />
                      <p className="text-xs text-gray-500 mt-1">{u.users_used} / {u.user_limit ?? '∞'}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {u.storage_gb_used.toFixed(1)} GB
                    </td>
                    <td className="px-4 py-4">
                      {org.at_aar_limit ? (
                        <span className="flex items-center space-x-1 text-xs font-semibold text-red-600">
                          <AlertTriangle className="w-3.5 h-3.5" /><span>Blocked</span>
                        </span>
                      ) : u.aar_pct >= 75 ? (
                        <span className="flex items-center space-x-1 text-xs font-semibold text-amber-600">
                          <AlertTriangle className="w-3.5 h-3.5" /><span>Warning</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-xs font-semibold text-emerald-600">
                          <CheckCircle className="w-3.5 h-3.5" /><span>OK</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={org.plan_tier}
                        onChange={e => changePlan(org.organization_id, e.target.value)}
                        disabled={updating === org.organization_id}
                        className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                      >
                        <option value="starter">Small Agency</option>
                        <option value="professional">Mid-Size Agency</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function OrgUsageBillingView() {
  const { organization } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organization?.organizationId) load();
  }, [organization]);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await fetchWithAuth('/usage/org', organization.organizationId);
      setData(resp);
    } catch { toast.error('Failed to load usage data'); }
    setLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw className="w-5 h-5 text-blue-500 animate-spin mr-3" />
      <span className="text-gray-500 text-sm">Loading usage…</span>
    </div>
  );

  if (!data) return null;

  const { plan_tier, plan_display, billing_period, usage: u, at_aar_limit, at_user_limit, at_hist_limit } = data;
  const anyAtLimit = at_aar_limit || at_user_limit || at_hist_limit;

  return (
    <div className="space-y-6">
      {anyAtLimit && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">You've reached a plan limit — some features are now restricted</p>
              <p className="text-xs text-red-600 mt-0.5">Upgrade your plan to restore full access.</p>
            </div>
          </div>
          <a href="https://withhazdex.com/packages" target="_blank" rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center space-x-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition">
            <ArrowUpRight className="w-4 h-4" />
            <span>Upgrade</span>
          </a>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Current Plan</p>
              <div className="flex items-center space-x-2 mt-0.5">
                <h3 className="text-lg font-bold text-gray-900">{plan_display}</h3>
              </div>
            </div>
          </div>
          <a href="https://withhazdex.com/packages" target="_blank" rel="noopener noreferrer"
            className="flex items-center space-x-1.5 px-4 py-2 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition">
            <ArrowUpRight className="w-4 h-4" />
            <span>Upgrade plan</span>
          </a>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'AARs / year', value: PLAN_LIMITS[plan_tier]?.aars },
            { label: 'Users', value: PLAN_LIMITS[plan_tier]?.users },
            { label: 'Historic docs', value: PLAN_LIMITS[plan_tier]?.historic_docs },
          ].map(({ label, value }) => (
            <div key={label} className="bg-gray-50 rounded-lg px-4 py-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{value === null ? '∞' : value.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        <h3 className="text-base font-semibold text-gray-900 flex items-center space-x-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          <span>Current Usage — {billing_period}</span>
        </h3>
        <MetricRow
          icon={FileText}
          label="AARs Generated"
          used={u.aars_used}
          limit={u.aar_limit}
          pct={u.aar_pct}
          iconColor="text-blue-500"
        />
        <div className="border-t border-gray-100" />
        <MetricRow
          icon={History}
          label="Historic Documents Uploaded"
          used={u.historic_docs_used}
          limit={u.historic_limit}
          pct={u.historic_pct}
          iconColor="text-indigo-500"
        />
        <div className="border-t border-gray-100" />
        <MetricRow
          icon={Users}
          label="Active Users"
          used={u.users_used}
          limit={u.user_limit}
          pct={u.user_pct}
          iconColor="text-emerald-500"
        />
        <div className="border-t border-gray-100" />
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Database className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-gray-700">Storage Used</span>
          </div>
          <span className="text-sm font-semibold text-gray-700">
            {parseFloat(u.storage_gb_used.toFixed(2))} GB
          </span>
        </div>
      </div>

      {plan_tier !== 'enterprise' && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white flex items-center justify-between">
          <div>
            <p className="font-semibold text-lg">Need more capacity?</p>
            <p className="text-blue-100 text-sm mt-1">Upgrade your plan to get higher limits and unlock advanced features.</p>
          </div>
          <a href="https://withhazdex.com/packages" target="_blank" rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center space-x-2 px-5 py-2.5 bg-white text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 transition">
            <ChevronRight className="w-4 h-4" />
            <span>View plans</span>
          </a>
        </div>
      )}
    </div>
  );
}